import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AudioProvider } from "../../contexts/Audio";
import TextareaListen from "../Pronunciation/TextareaListen";
import TextInstruction from "../Pronunciation/TextInstruction";
import RecState from "../Pronunciation/RecState";

// Popup with the same practice flow as the start page (record -> waveform ->
// analysis result), but for a fixed sentence instead of the free textarea.
// onClose receives whether the user finished at least one analysis.
// onNextSentence loads a fresh sentence; the parent remounts the modal with it.
const PracticeSentenceModal = ({
    sentence,
    selectedLanguage,
    onClose,
    onNextSentence,
    onResult,
    // When every sentence in the set has been practiced there is nothing left
    // to advance to, so "Next sentence" is replaced by a "Done" button.
    allPracticed = false,
}) => {
    const { t } = useTranslation();
    const [currentlyListening, setCurrentlyListening] = useState(false);
    const [currentlyRecording, setCurrentlyRecording] = useState(false);
    const [recordingState, setRecordingState] = useState("idle"); // "idle", "recording", "analyzing", "result"
    const [nextSentenceLoading, setNextSentenceLoading] = useState(false);
    // State (not a ref) so the "next sentence" button restyles once practiced
    const [practiced, setPracticed] = useState(false);

    const handleNextSentence = () => {
        if (nextSentenceLoading) return;
        setNextSentenceLoading(true);
        Promise.resolve(onNextSentence())
            .catch((error) => {
                console.error("Failed to load next sentence:", error);
            })
            .finally(() => setNextSentenceLoading(false));
    };

    // Closing mid-recording would leave the microphone stream running, so the
    // modal can only be dismissed via the recorder's own cancel button then.
    const canClose = recordingState !== "recording";

    useEffect(() => {
        if (recordingState === "result") {
            setPracticed(true);
        }
    }, [recordingState]);

    const handleClose = useCallback(() => {
        onClose(practiced);
    }, [onClose, practiced]);

    // Lock the page scroll behind the modal (important on mobile)
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape" && canClose) {
                handleClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [canClose, handleClose]);

    return (
        <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4"
            onClick={() => canClose && handleClose()}
        >
            <div
                className="relative w-full max-w-2xl max-h-[95vh] overflow-y-auto overflow-x-hidden rounded-2xl bg-white p-4 sm:p-6 md:p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-center">
                    <h2 className="text-xl md:text-2xl font-semibold">
                        {t("practiceModal.title")}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">{t("practiceModal.subtitle")}</p>
                </div>

                <div className="mt-4 md:mt-6 flex flex-col items-center gap-4 rounded-lg border border-border bg-secondary p-4 md:p-6">
                    <p className="text-lg md:text-xl font-medium text-center text-gray-800">
                        &ldquo;{sentence}&rdquo;
                    </p>
                    <TextareaListen
                        text={sentence}
                        selectedLanguage={selectedLanguage}
                        currentlyListening={currentlyListening}
                        setCurrentlyListening={setCurrentlyListening}
                        setTextareaHasError={() => {}}
                    />
                </div>

                {/* Same recording flow as the start page: mic button, live
                    waveform while recording, colored waveform + scores after */}
                <AudioProvider>
                    <TextInstruction
                        text={(() => {
                            switch (recordingState) {
                                case "idle":
                                    return t("textinstruction.click-to-record");
                                case "recording":
                                    return t("textinstruction.click-to-end");
                                default:
                                    return "";
                            }
                        })()}
                    />
                    <RecState
                        currentlyRecording={currentlyRecording}
                        setCurrentlyRecording={setCurrentlyRecording}
                        recordingState={recordingState}
                        setRecordingState={setRecordingState}
                        text={sentence}
                        selectedLanguage={selectedLanguage}
                        setTextareaHasError={() => {}}
                        visualizerMaxWidth={220}
                        stackResults
                        onResult={onResult}
                    />
                </AudioProvider>

                {canClose &&
                    (allPracticed ? (
                        // Nothing left to advance to: a single "Done" closes the modal.
                        <div className="mt-6 flex justify-center">
                            <button
                                className="px-6 py-2 rounded-md bg-primary text-white hover:bg-purple-600"
                                onClick={handleClose}
                            >
                                {t("practiceModal.done")}
                            </button>
                        </div>
                    ) : (
                        <div className="mt-6 flex items-center justify-between gap-4">
                            <button
                                className="border border-border px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 capitalize"
                                onClick={handleClose}
                            >
                                {t("cancel")}
                            </button>

                            {/* Skip (neutral) before practicing; primary once a result is in */}
                            <button
                                className={`flex items-center gap-1 px-4 py-2 rounded-md disabled:opacity-50 ${
                                    practiced
                                        ? "bg-primary text-white hover:bg-purple-600"
                                        : "border border-border text-gray-700 hover:bg-gray-100"
                                }`}
                                onClick={handleNextSentence}
                                disabled={nextSentenceLoading}
                            >
                                {t("practiceModal.nextSentence")}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                    className="size-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                                    />
                                </svg>
                            </button>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default PracticeSentenceModal;
